import CustomAutocomplete from "@/components/Form/InputFields/CustomAutocomplete";
import useDebounceSearch from "@/hooks/useDebounceSearch";
import { TName } from "@/types";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import * as React from "react";
import { FieldValues, PathValue, useWatch } from "react-hook-form";
import { IFormProps } from "../../targetingExpression.interface";

type Option = { label: string; value: string };
type QueryResult = { data?: Option[]; isFetching?: boolean; isLoading?: boolean };

/**
 * Server-searched variant of SelectModuleWithType, kept visually identical to it
 * (bordered card + inline include/exclude radios + a single multi-select) but
 * backed by a type-ahead endpoint instead of a preloaded static list. Used for
 * Beeswax geo region (state) and city, whose reference sets are too large to
 * preload.
 *
 * The field stores raw values (region `key` / city `geoname`) exactly like
 * `country`, so the payload generator and reverse map need no special handling.
 * A local cache keeps labels for selected values stable across searches, and
 * `useResolve` backfills labels for values already present on edit.
 */
export default function SelectAsyncModuleWithType<T extends FieldValues>({
  control,
  name,
  setValue,
  getValues,
  label,
  useSearch,
  useResolve,
  isReadOnly = false,
}: SelectAsyncModuleWithTypeProps<T>) {
  const [value, includeOrExclude] = useWatch({
    control,
    name: [`${name}.value` as TName<T>, `${name}.type` as TName<T>],
  });
  const selectedValues: string[] = React.useMemo(
    () => (Array.isArray(value) ? value : []),
    [value]
  );

  const { setSearch, debounceValue } = useDebounceSearch(500);
  const { data: searchOptions = [], isFetching } = useSearch(debounceValue);

  // value -> label cache so selected chips keep their label across searches.
  const [cache, setCache] = React.useState<Map<string, string>>(new Map());

  // Backfill labels for already-selected values not yet cached (edit prefill).
  const unresolved = React.useMemo(
    () => selectedValues.filter((v) => v != null && v !== "" && !cache.has(v)),
    [selectedValues, cache]
  );
  const { data: resolvedOptions = [] } = useResolve(unresolved);

  React.useEffect(() => {
    const incoming = [...searchOptions, ...resolvedOptions];
    if (!incoming.length) return;
    setCache((prev) => {
      let changed = false;
      const next = new Map(prev);
      incoming.forEach((o) => {
        if (o?.value != null && next.get(o.value) !== o.label) {
          next.set(o.value, o.label);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [JSON.stringify(searchOptions), JSON.stringify(resolvedOptions)]);

  // Keep `.type` in sync with whether anything is selected (mirrors
  // SelectModuleWithType).
  const onValueChange = () => {
    const [current, type] = getValues([
      `${name}.value`,
      `${name}.type`,
    ] as TName<T>[]);
    if (current?.length && !type) {
      setValue(`${name}.type` as TName<T>, "include" as PathValue<T, TName<T>>, {
        shouldDirty: true,
      });
    }
    if (!current?.length && type) {
      setValue(`${name}.type` as TName<T>, null as PathValue<T, TName<T>>, {
        shouldDirty: true,
      });
    }
  };

  // Options shown = current search results plus any cached (selected) options,
  // so selected values always have a resolvable label.
  const itemList = React.useMemo(() => {
    const map = new Map<string, string>();
    cache.forEach((lbl, val) => map.set(val, lbl));
    searchOptions.forEach((o) => o?.value != null && map.set(o.value, o.label));
    return Array.from(map, ([val, lbl]) => ({ label: lbl, value: val }));
  }, [cache, searchOptions]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        p: 2,
        backgroundColor: "background.paper",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          {label}
        </Typography>
        {selectedValues.length > 0 && (
          <FormControl size="small">
            <RadioGroup
              row
              value={includeOrExclude || "include"}
              onChange={(e) =>
                setValue(
                  `${name}.type` as TName<T>,
                  e.target.value as PathValue<T, TName<T>>,
                  { shouldDirty: true }
                )
              }
            >
              <FormControlLabel
                value="include"
                control={<Radio size="small" readOnly={isReadOnly} />}
                label="Include"
                sx={{
                  mr: 2,
                  "& .MuiFormControlLabel-label": {
                    fontSize: "0.875rem",
                    color:
                      includeOrExclude === "include"
                        ? "success.main"
                        : "text.secondary",
                  },
                }}
                disabled={isReadOnly}
              />
              <FormControlLabel
                value="exclude"
                control={<Radio size="small" readOnly={isReadOnly} />}
                label="Exclude"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "0.875rem",
                    color:
                      includeOrExclude === "exclude"
                        ? "error.main"
                        : "text.secondary",
                  },
                }}
                disabled={isReadOnly}
              />
            </RadioGroup>
          </FormControl>
        )}
      </Box>
      <CustomAutocomplete<T>
        name={`${name}.value` as TName<T>}
        label=""
        control={control}
        itemList={itemList as any}
        onSearch={(searchValue: string) => setSearch(searchValue)}
        onChange={onValueChange}
        loading={isFetching}
        readOnly={isReadOnly}
        type="secondary"
        placeholder="Type to search"
        disableClientFilter
        isMultiple
      />
    </Box>
  );
}

interface SelectAsyncModuleWithTypeProps<T extends FieldValues>
  extends Omit<IFormProps<T>, "isSubmitting"> {
  name: string;
  label: string;
  isReadOnly?: boolean;
  useSearch: (search: string) => QueryResult;
  useResolve: (values: string[]) => QueryResult;
}
